import { FeatureTour, TourState } from '@/types/tour'
import { ReleaseNote } from '@/types/releaseNotes'
import releaseNotesData from './release-notes-data.json'

const TOUR_STATE_KEY = 'datacentral_tour_state'

/**
 * Get tour state from localStorage
 */
export const getTourState = (): TourState => {
    try {
        const stored = localStorage.getItem(TOUR_STATE_KEY)
        if (stored) {
            return JSON.parse(stored)
        }
    } catch (error) {
        console.error('Error reading tour state:', error)
    }

    return {
        completedTours: {},
        lastSeenVersion: '',
    }
}

/**
 * Save tour state to localStorage
 */
export const saveTourState = (state: TourState): void => {
    try {
        localStorage.setItem(TOUR_STATE_KEY, JSON.stringify(state))
    } catch (error) {
        console.error('Error saving tour state:', error)
    }
}

/**
 * Check if a specific tour has been completed
 */
export const isTourCompleted = (tourId: string): boolean => {
    const state = getTourState()
    return !!state.completedTours[tourId]
}

/**
 * Mark a tour as completed
 */
export const markTourCompleted = (tourId: string): void => {
    const state = getTourState()
    state.completedTours[tourId] = true
    saveTourState(state)
}

/**
 * Reset a tour's completion status (for replaying)
 */
export const resetTourCompletion = (tourId: string): void => {
    const state = getTourState()
    delete state.completedTours[tourId]
    saveTourState(state)
}

/**
 * Get all release notes with tours
 */
export const getNotesWithTours = (): ReleaseNote[] => {
    const typedData = releaseNotesData as ReleaseNote[]
    return typedData.filter((note) => note.hasTour && note.tours?.length)
}

/**
 * Get tours for a specific version
 */
export const getToursByVersion = (version: string): FeatureTour[] | null => {
    const typedData = releaseNotesData as ReleaseNote[]
    const note = typedData.find((n) => n.version === version)
    return note?.tours || null
}

/**
 * Get a specific tour by version and tour ID
 */
export const getTourById = (
    version: string,
    tourId: string
): FeatureTour | null => {
    const tours = getToursByVersion(version)
    return tours?.find((t) => t.id === tourId) || null
}

/**
 * Get all available tours across all versions
 */
export const getAllTours = (): Array<FeatureTour & { version: string }> => {
    const typedData = releaseNotesData as ReleaseNote[]
    const allTours: Array<FeatureTour & { version: string }> = []

    for (const note of typedData) {
        if (note.tours?.length) {
            for (const tour of note.tours) {
                allTours.push({ ...tour, version: note.version })
            }
        }
    }

    return allTours
}

/**
 * Get pending tours for a specific route (not yet completed)
 */
export const getPendingToursForRoute = (
    route: string
): Array<FeatureTour & { version: string }> => {
    const allTours = getAllTours()
    const state = getTourState()

    return allTours.filter((tour) => {
        const isMatchingRoute = route.startsWith(tour.route)
        const isNotCompleted = !state.completedTours[tour.id]
        return isMatchingRoute && isNotCompleted
    })
}

/**
 * Check if there are any pending tours for the current user
 */
export const hasPendingTours = (): boolean => {
    const allTours = getAllTours()
    const state = getTourState()

    return allTours.some((tour) => !state.completedTours[tour.id])
}

/**
 * Get count of pending tours
 */
export const getPendingToursCount = (): number => {
    const allTours = getAllTours()
    const state = getTourState()

    return allTours.filter((tour) => !state.completedTours[tour.id]).length
}
